/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* BR: YouTube two-click placeholder (snippets/br-video-consent.liquid)
 * The iframe waits inside a <template> and is only inserted after the visitor clicks "load video", so no request
 * reaches YouTube before that. One delegated listener on document covers every placeholder, including sections
 * re-rendered by the theme editor (shopify:section:load), without re-binding.
 */
(() => {
  if (window.brVideoConsentBound) return;
  window.brVideoConsentBound = true;

  const loadVideo = (button) => {
    const wrapper = button.closest('[data-br-video-consent]');
    const template = wrapper ? wrapper.querySelector('template') : null;
    const source = template ? template.content.querySelector('iframe') : null;

    if (!source) return;

    const player = document.importNode(source, true);

    wrapper.removeAttribute('data-br-video-consent');
    wrapper.classList.add('is-loaded');
    wrapper.textContent = '';
    wrapper.appendChild(player);
    player.focus();
  };

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-br-video-consent-load]') : null;

    if (button) loadVideo(button);
  });
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * BR: GIFT MESSAGE (snippets/br-geschenknachricht.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Replaces the DOMContentLoaded block of the old custom.js (bugs B1/B2). Everything is delegated from `document`,
 * so it needs no element ids, binds to blocks that arrive later (theme editor section reloads) and never throws on
 * pages without the block.
 *
 * - The checkbox opens the text field. Unchecking clears the message.
 * - The counter shows "words / max" and cuts the text at the word limit (data-max-words, 30), as before.
 * - The add button is enabled only while the message has text.
 * - Submit guard: in the capture phase on `document`, before Focal's product-form handler on the form itself, the
 *   trimmed message is copied into properties[Geschenknachricht]. An empty message is never submitted, so the paid
 *   helper product cannot reach the cart without its message.
 * - After a successful add (variant:added) the panel closes and the message is cleared, so the same message cannot
 *   be charged twice by a second click.
 */
(() => {
  const ROOT = '[data-br-gift-message]';

  const partsOf = (root) => ({
    toggle: root.querySelector('[data-br-gift-message-toggle]'),
    panel: root.querySelector('[data-br-gift-message-panel]'),
    text: root.querySelector('[data-br-gift-message-text]'),
    counter: root.querySelector('[data-br-gift-message-counter]'),
    value: root.querySelector('[data-br-gift-message-value]'),
    submit: root.querySelector('[data-br-gift-message-submit]'),
  });

  const closestRoot = (target, selector) => {
    const element = target instanceof Element ? target.closest(selector) : null;
    return element ? element.closest(ROOT) : null;
  };

  const updateCounter = (root, parts) => {
    if (!parts.text) {
      return;
    }

    const max = parseInt(root.getAttribute('data-max-words'), 10) || 30;
    const words = parts.text.value.trim().split(/\s+/);
    let count = words[0] === '' ? 0 : words.length;

    if (count > max) {
      parts.text.value = words.slice(0, max).join(' ');
      count = max;
    }

    if (parts.counter) {
      parts.counter.textContent = `${count} / ${max}`;
    }
  };

  const updateSubmit = (parts) => {
    if (!parts.submit || !parts.text) {
      return;
    }

    const empty = parts.text.value.trim() === '';
    parts.submit.disabled = empty;
    parts.submit.classList.toggle('button--ternary', empty);
  };

  const sync = (root) => {
    const parts = partsOf(root);

    if (!parts.toggle || !parts.panel || !parts.text) {
      return;
    }

    const open = parts.toggle.checked;
    parts.panel.hidden = !open;
    parts.toggle.setAttribute('aria-expanded', String(open));

    if (!open) {
      parts.text.value = '';

      if (parts.value) {
        parts.value.value = '';
      }
    }

    updateCounter(root, parts);
    updateSubmit(parts);
  };

  const syncAll = (scope) => {
    const container = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    container.querySelectorAll(ROOT).forEach(sync);
  };

  document.addEventListener('change', (event) => {
    const root = closestRoot(event.target, '[data-br-gift-message-toggle]');

    if (root) {
      sync(root);
    }
  });

  document.addEventListener('input', (event) => {
    const root = closestRoot(event.target, '[data-br-gift-message-text]');

    if (root) {
      const parts = partsOf(root);
      updateCounter(root, parts);
      updateSubmit(parts);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const root = form instanceof HTMLFormElement ? form.closest(ROOT) : null;

    if (!root) {
      return;
    }

    const parts = partsOf(root);
    const message = parts.text ? parts.text.value.trim() : '';

    if (message === '' || !parts.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
      updateSubmit(parts);
      return;
    }

    parts.value.value = message;
  }, true);

  // After the helper line was added, Focal re-enables the add button (theme.js ProductForm._onSubmit). Closing and
  // clearing the panel keeps a second click from adding the paid helper product again with the same message.
  document.addEventListener('variant:added', (event) => {
    const root = event.target instanceof Element ? event.target.closest(ROOT) : null;

    if (!root) {
      return;
    }

    const parts = partsOf(root);

    if (parts.toggle) {
      parts.toggle.checked = false;
    }

    sync(root);
  });

  // Browsers restore checkbox and textarea state on back/forward navigation; the theme editor re-renders sections.
  document.addEventListener('shopify:section:load', (event) => syncAll(event.target));
  window.addEventListener('pageshow', () => syncAll());
  syncAll();
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * BR: ADD_TO_CART DATALAYER (snippets/br-add-to-cart-data.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * E06 B: replaces the inline onclick on the add to cart button of the old snippets/product-form.liquid. Focal fires
 * variant:added on the form after the cart accepted the item (product page, sticky bar, quick buy, featured product
 * and quick add in product cards). The event sends the same GA4 fields as before, but item_variant (SKU) and price
 * now come from the variant that was actually added, not from the start variant and the lowest product price.
 * Product-level fields come from the JSON in the form (br-add-to-cart-data); product cards without it fall back to
 * the cart line. The gift message line never had an event and still has none.
 *
 * Nothing is pushed until GTM has loaded after consent (window.brGtmLoaded, set by snippets/br-gtm.liquid): an event
 * queued before consent would otherwise reach the container retroactively once the visitor accepts on the same page.
 * Name, brand and category keep their quotes, like productDetails in br-gtm.
 */
(() => {
  const text = (value) => String(value == null ? '' : value);

  document.addEventListener('variant:added', (event) => {
    if (!window.brGtmLoaded) {
      return;
    }

    const item = event.detail ? event.detail.variant : null;

    if (!item) {
      return;
    }

    const form = event.target instanceof Element ? event.target.closest('form') : null;

    if (form && form.closest('[data-br-gift-message]')) {
      return;
    }

    let data = {};
    const island = form ? form.querySelector('script[data-br-add-to-cart-data]') : null;

    if (island) {
      try {
        data = JSON.parse(island.textContent) || {};
      } catch (error) {
        data = {};
      }
    }

    const cents = typeof item.price === 'number' ? item.price : parseInt(item.price, 10);
    const activeCurrency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : '';

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        items: [{
          item_id: String(data.item_id || item.product_id || ''),
          item_name: 'item_name' in data ? data.item_name : text(item.product_title),
          item_brand: 'item_brand' in data ? data.item_brand : text(item.vendor),
          item_category: data.item_category || '',
          item_variant: item.sku || '',
          currency: activeCurrency || data.currency || '',
          price: Number.isFinite(cents) ? String(cents / 100) : '',
        }],
      },
    });
  });
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * BR: EU LEGAL GUARANTEE NOTICE — KEYBOARD AND FALLBACK
 * ------------------------------------------------------------------------------------------------------------
 *
 * Ported unchanged from the old assets/custom.js (Focal 8.9.0); the notice markup (snippets/br-legal-guarantee.liquid)
 * is the same in Focal 13, so no selector had to change. All listeners sit on window/document and therefore survive
 * Focal's variant re-render and theme editor section reloads.
 *
 * snippets/br-legal-guarantee.liquid opens the official EU notice in a native [popover], which needs no JavaScript.
 * This block only covers what the popover cannot do on its own in this theme:
 *
 * - Escape: inside the cart drawer, Focal's focus trap listens for Escape on `document` in the capture phase,
 *   closes the drawer and cancels the key, so the notice stayed open on top of the page with no drawer behind it.
 *   This listener runs one step earlier (capture on `window`): while a notice is open, Escape closes just the
 *   notice and the drawer stays open. The keyup of the same keystroke is swallowed too.
 * - Tab: the popover is not modal, so Tab moves on from its last link to the checkout button hidden behind it.
 *   When focus leaves an open notice, the notice closes.
 * - Browsers without the Popover API (Safari < 17, Firefox < 125): the CSS hides the notice there, so it cannot
 *   push the checkout button out of the drawer, and the button opens the official notice file in a new tab.
 */
(() => {
  if (!HTMLElement.prototype.hasOwnProperty('popover')) {
    document.addEventListener('click', (event) => {
      const button = event.target instanceof Element && event.target.closest('.br-legal-guarantee__open');
      const notice = button && document.getElementById(button.getAttribute('popovertarget'));
      const file = notice && notice.querySelector('.br-legal-guarantee-popover__notice');

      if (file) {
        window.open(file.src, '_blank', 'noopener');
      }
    });

    return;
  }

  let swallowKeyup = false;

  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    const openNotice = document.querySelector('.br-legal-guarantee-popover:popover-open');

    if (!openNotice) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    openNotice.hidePopover();
    swallowKeyup = true;
  }, true);

  window.addEventListener('keyup', (event) => {
    if (event.key === 'Escape' && swallowKeyup) {
      event.stopImmediatePropagation();
      swallowKeyup = false;
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    const openNotice = document.querySelector('.br-legal-guarantee-popover:popover-open');

    if (openNotice && !openNotice.contains(event.target)) {
      openNotice.hidePopover();
    }
  });
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * BR: HEADER ACCOUNT DROPDOWN — ALIGN WITH THE NAVIGATION DROPDOWNS
 * ------------------------------------------------------------------------------------------------------------
 *
 * snippets/br-account-menu.liquid puts the account icon into Focal's <desktop-navigation>, so hover, touch and
 * keyboard work without code of our own. Only the vertical position needs help: the icon sits in the middle of the
 * header bar, the main navigation's dropdowns start at the bar's lower edge. This measures the distance (it depends
 * on the logo size setting) into --br-account-menu-offset; custom.css section 10 uses it for the dropdown's top and
 * for the hover bridge. Without JavaScript the dropdown simply opens right below the icon.
 */
(() => {
  if (window.brAccountMenuBound) return;
  window.brAccountMenuBound = true;

  const align = () => {
    document.querySelectorAll('.br-account-menu--icon .br-account-menu__item').forEach((item) => {
      const header = item.closest('store-header, .header');

      if (!header) return;

      const offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom - item.getBoundingClientRect().bottom));
      item.style.setProperty('--br-account-menu-offset', `${offset}px`);
    });
  };

  align();
  window.addEventListener('load', align);
  window.addEventListener('resize', align);
  document.addEventListener('shopify:section:load', align);
  document.addEventListener('mouseover', (event) => {
    if (event.target instanceof Element && event.target.closest('.br-account-menu--icon')) align();
  }, { passive: true });
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * BR: TINTED SECTIONS — DARKER ACCENT ON MATCHA AND CHAI BACKGROUNDS (redesign 2026)
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * The accent (#b0502a) reaches only about 4.1:1 on the Matcha and Chai section backgrounds. Every Focal section sets
 * its background as --section-background; this marks sections with a tinted (non page, non dark) background with
 * .br-on-tint, and custom.css section 11.2 switches their eyebrows and sale prices to --br-accent-on-tint (5% darker).
 * Without JavaScript the plain accent stays.
 */
(() => {
  if (window.brTintBound) return;
  window.brTintBound = true;

  const parse = (value) => (value || '').split(',').map((part) => parseInt(part, 10)).filter((n) => !Number.isNaN(n));
  const luminance = ([r, g, b]) => {
    const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  };

  const mark = (root = document) => {
    const rootStyle = getComputedStyle(document.documentElement);
    const page = parse(rootStyle.getPropertyValue('--background')).join(',');

    root.querySelectorAll('.shopify-section').forEach((section) => {
      const rgb = parse(getComputedStyle(section).getPropertyValue('--section-background'));
      const tinted = rgb.length === 3 && rgb.join(',') !== page && luminance(rgb) > 0.5 && luminance(rgb) < 0.9;
      section.classList.toggle('br-on-tint', tinted);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mark());
  else mark();
  document.addEventListener('shopify:section:load', () => mark());
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * BR: STICKY ADD-TO-CART BAR — SHOW IT ONLY AFTER THE REAL BUTTON HAS SCROLLED AWAY (redesign 2026)
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Focal's <product-sticky-form> watches the <form> around #MainPaymentContainer with an IntersectionObserver and
 * appears once that form has left the viewport. The redesign lays the buy area out as a grid in which that form is
 * display: contents (custom.css 14.5): the form has no box, so Focal's observer reported "passed" right away and the
 * bar showed from the start. This switches Focal's observer off and applies the same rules (theme.js
 * ProductStickyForm._setupVisibilityObservers) on scroll, once per frame, against the current payment container:
 * - shown once the add button has scrolled above the sticky header,
 * - on phones hidden again while the footer is visible.
 * Focal re-renders the bar and the container on variant change; both are looked up again on every check.
 */
(() => {
  if (window.brStickyFormBound) return;
  window.brStickyFormBound = true;

  const headerOffset = () => {
    const s = getComputedStyle(document.documentElement);
    const n = (name) => parseInt(s.getPropertyValue(name) || 0, 10) || 0;
    return n('--header-height') * n('--enable-sticky-header') + n('--announcement-bar-height') * n('--enable-sticky-announcement-bar');
  };

  let pending = false;

  const update = () => {
    pending = false;
    const target = document.getElementById('MainPaymentContainer');
    if (!target) return;

    const footer = document.querySelector('.shopify-section--footer');
    const passed = target.getBoundingClientRect().bottom <= headerOffset();
    const footerVisible = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
    const pocket = !!window.themeVariables && window.matchMedia(window.themeVariables.breakpoints.pocket).matches;
    const hide = pocket ? (!passed || footerVisible) : !passed;

    document.querySelectorAll('product-sticky-form').forEach((bar) => {
      if (bar.intersectionObserver && !bar.brObserverOff) {
        bar.intersectionObserver.disconnect();
        bar.brObserverOff = true;
      }
      if (bar.hasAttribute('hidden') !== hide) bar.hidden = hide;
    });
  };

  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('variant:changed', () => setTimeout(schedule, 50));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();

  // A bar re-rendered by Focal arrives with its own observer and state: re-check as soon as it is in the DOM
  new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches('product-sticky-form, #MainPaymentContainer') || n.querySelector?.('product-sticky-form, #MainPaymentContainer'))))) schedule();
  }).observe(document.body, { childList: true, subtree: true });
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * BR: DRAWER CROSS-SELL ARROWS
 * ------------------------------------------------------------------------------------------------------------
 *
 * From 1000px the "Das passt dazu" cross-sell in the cart drawer is a horizontal card strip (redesign R20,
 * snippets/br-drawer-cross-sell.liquid). Touch devices swipe it; mouse users get the previous/next arrows next to the
 * heading (R32). A click scrolls by one card (card width plus the grid gap); an arrow is disabled at its end and both
 * are disabled when every card fits. Focal re-renders the drawer on every cart change, so the state is refreshed on
 * scroll, hover and focus instead of being bound once. Right-to-left languages scroll the other way (their end state
 * falls back to scrollWidth).
 */
(() => {
  if (window.brCrossSellArrowsBound) return;
  window.brCrossSellArrowsBound = true;

  // The end of the strip is the last card's edge plus the end padding, not scrollWidth: the closed quick-buy drawer
  // inside a card (Focal, fixed and translated off-canvas) adds empty scrollable space after the last card.
  const scrollRange = (scroller) => {
    const cards = scroller.querySelectorAll('.mini-cart__recommendations-list > .product-item');
    const last = cards[cards.length - 1];

    if (!last || getComputedStyle(scroller).direction === 'rtl') return scroller.scrollWidth - scroller.clientWidth;

    const inner = scroller.querySelector('.scroller__inner');
    const endPadding = inner ? parseFloat(getComputedStyle(inner).paddingInlineEnd) || 0 : 0;
    const lastEdge = last.getBoundingClientRect().right - scroller.getBoundingClientRect().left + scroller.scrollLeft;

    return Math.max(0, Math.round(lastEdge + endPadding - scroller.clientWidth));
  };

  const update = (root) => {
    const scroller = root.querySelector('.scroller');
    if (!scroller) return;

    const max = scrollRange(scroller);
    const position = Math.abs(scroller.scrollLeft);

    root.querySelectorAll('[data-br-cross-sell-step]').forEach((button) => {
      const backwards = Number(button.getAttribute('data-br-cross-sell-step')) < 0;
      button.disabled = max <= 1 || (backwards ? position <= 1 : position >= max - 1);
    });
  };

  const refreshAll = () => document.querySelectorAll('.br-drawer-cross-sell').forEach(update);

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-br-cross-sell-step]') : null;
    const root = button ? button.closest('.br-drawer-cross-sell') : null;
    const scroller = root ? root.querySelector('.scroller') : null;

    if (!scroller) return;

    const card = scroller.querySelector('.product-item');
    const gap = card ? parseFloat(getComputedStyle(card.parentElement).columnGap) || 0 : 0;
    const distance = card ? card.getBoundingClientRect().width + gap : scroller.clientWidth;
    const direction = Number(button.getAttribute('data-br-cross-sell-step')) * (getComputedStyle(scroller).direction === 'rtl' ? -1 : 1);
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    scroller.scrollBy({ left: distance * direction, behavior: smooth ? 'smooth' : 'auto' });
  });

  document.addEventListener('scroll', (event) => {
    const target = event.target;
    if (target instanceof Element && target.matches('.br-drawer-cross-sell .scroller')) update(target.closest('.br-drawer-cross-sell'));
  }, true);

  ['mouseover', 'focusin'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if (event.target instanceof Element && event.target.closest('.br-drawer-cross-sell')) refreshAll();
    }, { passive: true });
  });

  refreshAll();
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * BR: DRAWER CROSS-SELL QUICK BUY — OPEN OUTSIDE THE CARD (R35)
 * ------------------------------------------------------------------------------------------------------------
 *
 * The "+" of a product with several variants in the cart drawer cross-sell ("Das passt dazu",
 * snippets/br-drawer-cross-sell.liquid; Focal's dynamic drawer recommendations too) is Focal's toggle-button for the
 * quick-buy drawer (from 741px) or quick-buy popover (phones) rendered inside the card (snippets/product-item.liquid).
 * The translated "+" wrapper (custom.css 17.3) is the containing block of those fixed elements and the strip's
 * scroller clips them, so the quick buy opened inside the small card (owner report R35, "Bio Tee Adventskalender").
 *
 * In the capture phase, before Focal's toggle-button handles the click, the element moves to <body> (a comment keeps
 * its place in the card) and opens there over the cart drawer like the quick buy of a collection page. Focal finds it
 * by id (theme.js ToggleButton.controlledElement), so the loading bar, aria-expanded and closing keep working. After
 * the close transition the used element is dropped and the card gets a fresh copy (same id and href): Focal leaves a
 * one-time transitionend listener on a closed quick buy that would empty its next opening. If Focal re-rendered the
 * drawer meanwhile (after an add the card is gone, after an error the new card has its own copy), nothing goes back.
 * While it is outside the card this block also:
 * - pauses the cart drawer's focus trap as soon as the quick buy opens: Focal starts the quick buy's own trap only
 *   after 150 ms, and until then a tap or Escape inside it counted as "outside" and closed the cart drawer;
 * - sets html.lock-all again after the quick-buy drawer closed (Focal's DrawerContent removes it although the cart
 *   drawer is still open) and ends a quick-buy trap that Focal's 150 ms timer started after a very fast close;
 * - loads the product only once: after the load the href moves into this block's bookkeeping (the fresh copy gets it
 *   back). Every earlier render of the cart drawer leaves a detached "+" whose document listener (Focal ToggleButton)
 *   sets open again on the element with its id; with the href each of them reloaded the quick buy, took the focus and
 *   reset a variant picked meanwhile, so the wrong variant could be added;
 * - after a successful add while the cart drawer is open: closes the quick-buy drawer (Focal closes only the popover),
 *   keeps Focal's 600 ms re-render delay and stops the add from replaying the cart drawer's opening animation (the
 *   form is no longer inside a .drawer, so Focal asks to open the cart drawer, which is already open), then shows
 *   the new cart line. If the cart drawer was closed meanwhile, the quick buy stays open with Focal's notification;
 * - shows an add error in the phone sheet (it has no notification of its own, Focal's global one sits behind the cart
 *   drawer); the text is Shopify's description, inserted as text;
 * - closes the quick buy when the cart drawer closes, at the 740px switch (also one that finished loading after the
 *   switch and would open hidden) and on a theme editor reload of mini-cart.
 * After a Focal update compare theme.js OpenableElement, ToggleButton, DrawerContent, QuickBuyDrawer/QuickBuyPopover,
 * ProductForm and CartDrawer._rerenderCart (docs/CUSTOMIZATIONS.md, R35).
 */
(() => {
  if (window.brCrossSellQuickBuyBound) return;
  window.brCrossSellQuickBuyBound = true;

  const TRIGGER = '#mini-cart .mini-cart__recommendations button[is="toggle-button"][aria-controls]';
  const QUICK_BUY = 'quick-buy-drawer, quick-buy-popover';
  const lifted = new Map(); // quick buy in <body> -> { marker, closing, timer, href }
  let addedFromLifted = 0; // time of the last add from a lifted quick buy while the cart drawer was open
  const cartDrawer = () => document.getElementById('mini-cart');
  const cartIsOpen = () => {
    const cart = cartDrawer();
    return Boolean(cart && cart.hasAttribute('open'));
  };
  const closeLifted = () => lifted.forEach((entry, quickBuy) => {
    if (quickBuy.hasAttribute('open')) quickBuy.open = false;
  });

  // After the close transition: a fresh copy goes back into the card, the used element is dropped
  const settle = (quickBuy) => {
    const entry = lifted.get(quickBuy);
    if (!entry || !entry.closing || quickBuy.hasAttribute('open')) return;
    clearTimeout(entry.timer);
    lifted.delete(quickBuy);
    if (entry.marker.isConnected) {
      const fresh = quickBuy.cloneNode(false);
      if (entry.href) fresh.setAttribute('href', entry.href);
      entry.marker.replaceWith(fresh);
    }
    quickBuy.remove();
  };

  // Focal re-renders the cart drawer after the add and keeps its scroll position: bring the new line into view and
  // give keyboard focus a place (the "+" it came from is gone with the re-render)
  const revealAddedLine = (item) => {
    const cart = cartDrawer();
    const variantId = item ? String(item.variant_id || item.id || '') : '';
    if (!cart || !variantId) return;

    const observer = new MutationObserver(() => {
      observer.disconnect();
      const link = Array.from(cart.querySelectorAll('.line-item .product-item-meta__title')).find((title) => {
        try {
          return new URL(title.href, window.location.href).searchParams.get('variant') === variantId;
        } catch (error) {
          return false;
        }
      });
      const content = cart.querySelector('.drawer__content');
      if (!link || !content) return;

      const line = link.closest('.line-item') || link;
      const top = line.getBoundingClientRect().top - content.getBoundingClientRect().top + content.scrollTop;
      if (top < content.scrollTop || top + line.offsetHeight > content.scrollTop + content.clientHeight) {
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        content.scrollTo({ top: Math.max(0, top - 16), behavior: smooth ? 'smooth' : 'auto' });
      }
      if (!cart.contains(document.activeElement)) link.focus({ preventScroll: true });
    });

    observer.observe(cart, { childList: true });
    setTimeout(() => observer.disconnect(), 8000);
  };

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest(TRIGGER) : null;
    if (!button || button.getAttribute('aria-expanded') === 'true') return; // only the opening click

    const id = button.getAttribute('aria-controls');
    let quickBuy = document.getElementById(id);
    const entry = quickBuy ? lifted.get(quickBuy) : null;

    if (entry && !entry.closing) return; // still in <body> after a failed load: Focal retries it there
    if (entry) { // reopened while sliding out: drop it now, the card gets a fresh copy
      settle(quickBuy);
      quickBuy = document.getElementById(id);
    }

    if (!quickBuy || !quickBuy.matches(QUICK_BUY)) return;
    const card = button.closest('.product-item');
    if (!card || !card.contains(quickBuy)) return;

    const marker = document.createComment(' BR: quick buy is in <body> while in use ');
    quickBuy.before(marker);
    document.body.appendChild(quickBuy);
    lifted.set(quickBuy, { marker, closing: false, timer: 0, href: '' });
  }, true); // capture: before ToggleButton's click listener on the button itself

  // Capture on document: runs before the ToggleButton listeners on document, including the detached ones
  document.addEventListener('openable-element:open', (event) => {
    const quickBuy = event.target;
    const entry = lifted.get(quickBuy);
    if (!entry) return;

    if (quickBuy.hasAttribute('href')) { // loaded: later open = true calls must not load it again
      entry.href = quickBuy.getAttribute('href');
      quickBuy.removeAttribute('href');
    }

    if (!quickBuy.getClientRects().length) { // finished loading after the 740px switch: hidden by its breakpoint class
      queueMicrotask(() => { quickBuy.open = false; });
      return;
    }

    if (!cartIsOpen()) return;
    const trap = cartDrawer().focusTrap;
    if (trap.active && !trap.paused) trap.pause(); // the trap stack resumes it when the quick buy's trap ends
  }, true);

  document.addEventListener('openable-element:close', (event) => {
    const quickBuy = event.target;
    if (quickBuy === cartDrawer()) {
      closeLifted();
      return;
    }

    const entry = lifted.get(quickBuy);
    if (!entry) return; // nested openables (variant combo-box, ...) bubble through the quick buy
    entry.closing = true;

    // Runs after DrawerContent removed lock-all, before the next paint
    queueMicrotask(() => {
      if (!cartIsOpen()) return;
      document.documentElement.classList.add('lock-all');
      const trap = cartDrawer().focusTrap;
      if (trap.paused && !quickBuy.focusTrap.active) trap.unpause(); // closed before its own trap started
    });
    setTimeout(() => {
      if (!quickBuy.hasAttribute('open') && quickBuy.focusTrap.active) quickBuy.focusTrap.deactivate();
    }, 200);

    const onEnd = (transition) => {
      if (transition.target !== quickBuy) return;
      quickBuy.removeEventListener('transitionend', onEnd);
      settle(quickBuy);
    };
    quickBuy.addEventListener('transitionend', onEnd);
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
      quickBuy.removeEventListener('transitionend', onEnd);
      settle(quickBuy);
    }, 1000); // no transitionend when it closes hidden (breakpoint class) or without a transition
  }, true);

  document.addEventListener('variant:added', (event) => {
    const quickBuy = event.target instanceof Element ? event.target.closest(QUICK_BUY) : null;
    if (!quickBuy || !lifted.has(quickBuy) || !cartIsOpen()) return; // cart closed meanwhile: Focal's notification

    // Inside the card the event passed through <cart-drawer>, which then waits 600 ms before re-rendering
    cartDrawer().nextReplacementDelay = 600;
    addedFromLifted = Date.now();
    if (quickBuy.hasAttribute('open')) quickBuy.open = false; // closed before cart-notification:show: no toast
    revealAddedLine(event.detail ? event.detail.variant : null);
  });

  // ProductForm asks to open the cart drawer when the form is not inside a .drawer (the lifted phone sheet). The cart
  // drawer is open already and would replay its opening animation (blank lines, scroll to the top). Capture on
  // document runs before CartDrawer's listener on <html>; the detail object is the one it receives.
  document.addEventListener('cart:refresh', (event) => {
    if (!addedFromLifted) return;
    const recent = Date.now() - addedFromLifted < 10000;
    addedFromLifted = 0;
    if (recent && event.detail && event.detail.openMiniCart && cartIsOpen()) event.detail.openMiniCart = false;
  }, true);

  document.addEventListener('cart-notification:show', (event) => {
    const quickBuy = event.target instanceof Element ? event.target.closest('quick-buy-popover') : null;
    const detail = event.detail || {};
    if (!quickBuy || !lifted.has(quickBuy) || detail.status !== 'error' || !detail.error) return;

    const anchor = quickBuy.querySelector('.product-form__quantity') || event.target.closest('.product-form__buy-buttons');
    if (!anchor) return;
    let message = quickBuy.querySelector('.br-quick-buy-error');
    if (!message) {
      message = document.createElement('p');
      message.className = 'br-quick-buy-error';
      message.setAttribute('role', 'alert');
      anchor.before(message);
    }
    message.textContent = detail.error;
  }, true);

  document.addEventListener('variant:changed', (event) => {
    const quickBuy = event.target instanceof Element ? event.target.closest('quick-buy-popover') : null;
    const message = quickBuy ? quickBuy.querySelector('.br-quick-buy-error') : null;
    if (message) message.remove();
  });

  window.matchMedia('(max-width: 740px)').addEventListener('change', closeLifted);
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.detail && event.detail.sectionId === 'mini-cart') closeLifted();
  });
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * BR: RIVYO REVIEWS ON SCROLL (performance phase 2)
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * sections/apps.liquid renders Rivyo's review section without its id (data-br-deferred-id), so the Rivyo app embed
 * skips it when it scans the page on DOMContentLoaded. Before, that scan fetched the reviews and 20+ review photos on
 * page load, far below the fold. Here the id comes back once the section is 800px from the viewport, and Rivyo's
 * own window.rivyoProductReviewBlock() mounts it (Rivyo guards against mounting an element twice).
 * A click on Rivyo's star badge under the title starts the section at once, so the badge's scroll to
 * #wc_review_section still finds it. Without JavaScript or without the app nothing is rendered, as before.
 */
(() => {
  if (window.brRivyoDeferBound) return;
  window.brRivyoDeferBound = true;

  const start = (element) => {
    if (!element.hasAttribute('data-br-deferred-id')) return;
    element.id = element.getAttribute('data-br-deferred-id');
    element.removeAttribute('data-br-deferred-id');
    if (typeof window.rivyoProductReviewBlock === 'function') window.rivyoProductReviewBlock();
  };

  const setup = () => {
    const pending = Array.from(document.querySelectorAll('[data-br-deferred-id]'));
    if (!pending.length) return;

    if (!('IntersectionObserver' in window)) {
      pending.forEach(start);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        start(entry.target);
      });
    }, { rootMargin: '800px 0px' });
    pending.forEach((element) => observer.observe(element));

    // Capture phase: runs before Rivyo's badge handler, which scrolls to #wc_review_section
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('.rivyo-badge-container, .wc_product_review_badge')) return;
      document.querySelectorAll('[data-br-deferred-id]').forEach((element) => {
        observer.unobserve(element);
        start(element);
      });
    }, true);
  };

  // Rivyo's script is deferred like this file and scans on DOMContentLoaded; setting up after that scan means the
  // section is always mounted by rivyoProductReviewBlock() and never by the scan
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(setup, 0));
  else setTimeout(setup, 0);
})();
